<!-- タイトルと新規作成ボタン -->
<div class="title-area">
  <h2 class="title">組合相談</h2>
  <button mat-raised-button color="warn" [disabled]="showSpinner" (click)="onCreate()">
    新規作成
  </button>
</div>

<!-- スピナー -->
@if (showSpinner) {
  <div class="spinner">
    <mat-spinner></mat-spinner>
  </div>
} @else {
  <table class="list-table">
    <thead>
      <tr>
        <th class="col-status">ステータス</th>
        <th class="col-result">結果</th>
        <th class="col-content">相談内容</th>
        <th class="col-consultant">相談者</th>
        <th class="col-urgency">緊急度</th>
        <th class="col-scope">共有範囲</th>
        <th class="col-dates">相談日など</th>
      </tr>
    </thead>
    <tbody>
      @for (item of items; track item.id) {
        <tr>
          <!-- ステータス -->
          <td class="col-status">
            <div class="cell-center">{{ item.status }}</div>
          </td>

          <!-- 結果 -->
          <td class="col-result">
            <div class="cell-center">
              <!-- 列が狭く1行に収まらない場合は、「解決不可/方向」のように「方向」の前で改行する -->
              @for (part of splitResultType(item.resultType); track $index) {
                <span class="result-part">{{ part }}</span>
              }
            </div>
          </td>

          <!-- 相談内容 -->
          <td class="col-content">
            <div class="content-area">
              <a class="content-title" (click)="onItemClick(item)">{{ item.title }}</a>
              @if (item.categoryNames?.length) {
                <div class="category-tags">
                  @for (name of item.categoryNames; track $index) {
                    <span class="category-tag">{{ name }}</span>
                  }
                </div>
              }
              <!-- 概要：1440px以上は116文字、1024〜1439pxは50文字まで -->
              <div class="content-summary desc-long">{{ getTruncatedSummary(item.summary) }}</div>
              <div class="content-summary desc-short">{{ getTruncatedSummary(item.summary, 50) }}</div>
            </div>
          </td>

          <!-- 相談者 -->
          <td class="col-consultant">
            <!-- 名前・年代 性別の下に間隔をあけて所属を並べる（MYイベント一覧の起票者と同じ並び） -->
            <div class="consultant-area">
              <div class="consultant-name">{{ item.consultantName }}</div>
              <div class="consultant-meta">{{ item.consultantAgeGroup }} {{ item.consultantGender }}</div>
              <!-- 所属は1つ目だけ表示する -->
              @if (item.consultantAffiliation?.length) {
                <div class="consultant-affiliation">{{ item.consultantAffiliation[0] }}</div>
              }
            </div>
          </td>

          <!-- 緊急度 -->
          <td class="col-urgency">
            <div class="cell-center">{{ item.urgency }}</div>
          </td>

          <!-- 共有範囲 -->
          <td class="col-scope">
            <div class="scope-area">
              <div>自組織</div>
              <div>管理以上</div>
              @if (item.additionalMembers?.length) {
                <div>他{{ item.additionalMembers.length }}名</div>
              }
            </div>
          </td>

          <!-- 相談日など -->
          <td class="col-dates">
            <div class="dates-area">
              @if (item.consultDates?.length) {
                <div class="consult-dates">{{ getDisplayConsultDates(item.consultDates) }}</div>
              }
              @if (item.updatedAt) {
                <div class="last-updated">
                  <span class="last-updated-label">最終更新日</span>
                  <span class="last-updated-date">{{ item.updatedAt | date:'yyyy/MM/dd' }}</span>
                </div>
              }
            </div>
          </td>
        </tr>
      }
    </tbody>
  </table>

  <!-- タブレット・SP（1023px以下）用：1件1枚のカード（表は非表示） -->
  <div class="item-cards">
    @for (item of items; track item.id) {
      <div class="item-card">
        <!-- カード上部：結果（白・枠）・ステータス（灰色の帯）・緊急度（帯）、件名。
             結果・緊急度の帯がない場合は詰める（概要は表示しない） -->
        <div class="card-head">
          <div class="card-tags">
            @if (item.resultType) {
              <span class="card-result">{{ item.resultType }}</span>
            }
            <span class="card-status">{{ item.status }}</span>
            <!-- 緊急度：「非常に高い」は「超緊急」（赤）、「高い」は「緊急」（紺）。通常・低いは出さない -->
            @if (getUrgencyBadge(item.urgency); as badge) {
              <span class="card-urgency" [class.high]="badge.high">{{ badge.label }}</span>
            }
          </div>
          <a class="content-title" (click)="onItemClick(item)">{{ item.title }}</a>
        </div>
        <!-- 区切り線より下：カテゴリー（1行に1つ）、相談者、相談日、共有範囲、最終更新日 -->
        <div class="card-body">
          <div class="card-label">カテゴリー</div>
          <div class="card-value">
            @for (name of item.categoryNames; track $index) {
              <div>{{ name }}</div>
            }
          </div>

          <!-- 相談者：名前・年代 性別・所属（1つ目だけ）の順で1行ずつ（最大4行で、超える分は枠内スクロール） -->
          <div class="card-label">相談者</div>
          <div class="card-value card-consultant">
            <div>{{ item.consultantName }}</div>
            <div>{{ item.consultantAgeGroup }} {{ item.consultantGender }}</div>
            @if (item.consultantAffiliation?.length) {
              <div>{{ item.consultantAffiliation[0] }}</div>
            }
          </div>

          <div class="card-label">相談日</div>
          <div class="card-value consult-dates">
            @if (item.consultDates?.length) {
              {{ getDisplayConsultDates(item.consultDates) }}
            }
          </div>

          <!-- 共有範囲：「他N名」も「管理以上」と同じ黒・同じ大きさ -->
          <div class="card-label">共有範囲</div>
          <div class="card-value">
            自組織 管理以上
            @if (item.additionalMembers?.length) {
              <span>他{{ item.additionalMembers.length }}名</span>
            }
          </div>

          <div class="card-label">最終更新日</div>
          <div class="card-value">
            @if (item.updatedAt) {
              {{ item.updatedAt | date:'yyyy/MM/dd' }}
            }
          </div>
        </div>
      </div>
    }
  </div>

  @if (items.length === 0) {
    <div class="no-items">相談がありません。</div>
  }
}
